import ModeToolPointEditor from "@components/mode_tool/pointEditor";
import {
  IconDeviceFloppy,
  IconDownload,
  IconMusic,
  IconPlus,
  IconRoute,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useRef, useState } from "react";
import {
  hasRoutePointMedia,
  hasRoutePointName,
  hasRoutePointRadius,
  hasRoutePointTurnCase,
  hasRoutePointType,
  isDirectionRoutePoint,
} from "@utils/mode_tool/validation";
import { normalizeDrivingRouteGroupKey } from "@utils/mode_tool/modeRoute";

function getPointValidationIssues(point = {}, selectedMode = "") {
  const issues = [];
  if (!hasRoutePointName(point?.name, point)) {
    issues.push("name");
  }
  if (selectedMode !== "walking") {
    if (!hasRoutePointRadius(point?.radiusMeters)) issues.push("radius > 0");
    if (!hasRoutePointTurnCase(point?.turnCase)) issues.push("turn type");
  }
  if (!isDirectionRoutePoint(point) && !hasRoutePointMedia(point)) {
    issues.push("1 image or audio");
  }
  if (!hasRoutePointType(point?.pointType, selectedMode)) {
    issues.push("point type");
  }
  return issues;
}

function resolveRouteGroupDisplay(point = {}) {
  const routeGroup = normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    Number(point?.order ?? 0) || 0,
  );
  const routeGroupName = String(
    point?.routeGroupName ?? point?.route_group_name ?? "",
  ).trim();
  if (!routeGroup && !routeGroupName) return "";
  if (routeGroup === "main") return "";
  return routeGroupName;
}

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    return entry.value || entry.message || entry.text || "";
  }
  return "";
}

function formatDrivingDuration(durationSeconds) {
  const seconds = Number(durationSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${remainingSeconds} min`;
}

function ModeToolPointsList({
  selectedMode,
  modePoints,
  directiosApiRoutePointRaw,
  selectedPointId,
  onSelectPoint,
  languages,
  activeLanguageIso,
  onCommitPoint,
  onTranslatePoint,
  onGeneratePointImage,
  onGeneratePointMusic,
  isGeneratingMusic = false,
  generatingMusicPointId = "",
  onMixAudioMusic,
  mixingPointId = "",
  mixingLanguageIso = "",
  audioMixErrorByPointLanguage = {},
  onUploadPointMedia,
  onDeletePoint,
  onSaveModeRedux,
  canSaveModePoints,
  isBatchAudioBusy = false,
  modeValidationHint,
  saveModePointsHint,
  walkingRouteKeys,
  selectedWalkingRouteKey,
  walkingRouteMetaByKey,
  isWalkingRouteShapeEditEnabled,
  onSelectWalkingRoute,
  onAddWalkingRoute,
  onChangeWalkingRouteName,
  onToggleWalkingRouteShapeEdit,
  onStopWalkingRouteShapeEdit,
  onDeleteWalkingRoute,
  onDownloadBatchAudios,
  isDownloadingBatchAudios = false,
  onUploadBatchAudios,
  isUploadingBatchAudios = false,
  batchAudioStatus = "",
  batchAudioError = "",
}) {
  const [isSaving, setIsSaving] = useState(false);
  const batchAudioZipInputRef = useRef(null);
  if (!selectedMode) return null;
  const activeWalkingRouteMeta =
    walkingRouteMetaByKey?.[selectedWalkingRouteKey] || {};
  const activeWalkingRouteNameValue = readLocalizedValue(
    activeWalkingRouteMeta?.name?.[activeLanguageIso],
  );
  const drivingDurationByPointId = new Map();
  if (selectedMode === "driving") {
    Object.values(directiosApiRoutePointRaw?.routes || {}).forEach((route) => {
      (Array.isArray(route?.pointDurations) ? route.pointDurations : []).forEach(
        (row) => {
          const pointId = String(row?.pointId || "");
          if (pointId) {
            drivingDurationByPointId.set(
              pointId,
              formatDrivingDuration(row?.durationSeconds),
            );
          }
        },
      );
    });
  }

  return (
    <fieldset
      className="mode-route-tool-busy-fieldset"
      disabled={isBatchAudioBusy}
      aria-busy={isBatchAudioBusy}
    >
      <div className="mode-route-tool-points">
        {/*  <p className="mode-route-tool-subtitle">
        Type - {String(selectedMode || "").toLowerCase()}
      </p> */}

        {["walking", "driving", "random"].includes(selectedMode) ? (
          <div className="mode-route-tool-routes">
            {selectedMode !== "random" ? (
              <div className="mode-route-tool-routes-head">
                <p className="mode-route-tool-subtitle">
                  {selectedMode === "walking" ? "Routes" : "Manual route"}
                </p>
                <div className="mode-route-tool-routes-actions">
                  {/* Creates a local unsaved walking route (new-*) that can be edited before saving. */}
                  <button
                    type="button"
                    className={`app-icon-btn app-icon-btn--add ${isWalkingRouteShapeEditEnabled ? "is-active" : ""}`}
                    aria-label={
                      isWalkingRouteShapeEditEnabled
                        ? "Stop drawing manual route"
                        : "Draw manual route"
                    }
                    title={
                      isWalkingRouteShapeEditEnabled
                        ? "Stop drawing manual route"
                        : "Draw manual route"
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleWalkingRouteShapeEdit?.();
                    }}
                  >
                    <IconRoute size={14} />
                  </button>
                  {selectedMode === "walking" ? (
                    <button
                      type="button"
                      className="app-icon-btn app-icon-btn--add"
                      aria-label="Add walking route"
                      title="Add walking route"
                      onClick={(event) => {
                        event.stopPropagation();
                        onStopWalkingRouteShapeEdit?.();
                        onAddWalkingRoute?.();
                      }}
                    >
                      <IconPlus size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="mode-route-tool-routes-head mode-route-tool-batch-audio-head">
              <p className="mode-route-tool-subtitle">Batch Audio</p>
              <div className="mode-route-tool-routes-actions mode-route-tool-batch-audio-actions">
                <button
                  type="button"
                  className="mode-route-tool-batch-audio-btn"
                  aria-label="Download Batch Audios"
                  title={isDownloadingBatchAudios ? "Downloading batch audios" : "Download Batch Audios"}
                  disabled={isDownloadingBatchAudios || isUploadingBatchAudios}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDownloadBatchAudios?.();
                  }}
                >
                  {isDownloadingBatchAudios ? (
                    <span className="mode-route-tool-btn-spinner" />
                  ) : (
                    <>
                      <IconMusic size={14} />
                      <IconDownload size={14} />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="mode-route-tool-batch-audio-btn"
                  aria-label="Upload Batch Audios"
                  title={isUploadingBatchAudios ? "Uploading batch audios" : "Upload Batch Audios"}
                  disabled={isDownloadingBatchAudios || isUploadingBatchAudios}
                  onClick={(event) => {
                    event.stopPropagation();
                    batchAudioZipInputRef.current?.click();
                  }}
                >
                  {isUploadingBatchAudios ? (
                    <span className="mode-route-tool-btn-spinner" />
                  ) : (
                    <>
                      <IconMusic size={14} />
                      <IconUpload size={14} />
                    </>
                  )}
                </button>
                <input
                  ref={batchAudioZipInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  className="mode-route-tool-batch-audio-file-input"
                  aria-label="Choose batch audio ZIP"
                  onChange={(event) => {
                    const [zipFile] = Array.from(event.target.files || []);
                    if (zipFile) onUploadBatchAudios?.(zipFile);
                    event.target.value = "";
                  }}
                />
              </div>
            </div>
            {batchAudioStatus || batchAudioError ? (
              <p
                className={`mode-route-tool-batch-audio-feedback ${batchAudioError ? "is-error" : "is-success"}`}
                role={batchAudioError ? "alert" : "status"}
                aria-live="polite"
              >
                {batchAudioError || batchAudioStatus}
              </p>
            ) : null}
            {selectedMode === "walking" ? (
              <div className="mode-route-tool-route-tabs">
                {(walkingRouteKeys || []).map((routeKey, index) => (
                  <div
                    key={routeKey}
                    className={`mode-route-tool-route-chip ${selectedWalkingRouteKey === routeKey ? "active" : ""}`}
                  >
                    {/* Select route to load/display only that route's points. */}
                    <button
                      type="button"
                      className={`mode-route-tool-route-tab ${selectedWalkingRouteKey === routeKey ? "active" : ""}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onStopWalkingRouteShapeEdit?.();
                        onSelectWalkingRoute?.(routeKey);
                      }}
                    >
                      {readLocalizedValue(
                        walkingRouteMetaByKey?.[routeKey]?.name?.[
                          activeLanguageIso
                        ],
                      ) || `Route ${index + 1}`}
                    </button>
                    <button
                      type="button"
                      className="mode-route-tool-route-chip-delete"
                      aria-label={`Delete Route ${index + 1}`}
                      title={`Delete Route ${index + 1}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onStopWalkingRouteShapeEdit?.();
                        // For local routes this removes only client state; persisted routes call backend delete.
                        onDeleteWalkingRoute?.(routeKey);
                      }}
                    >
                      <IconTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {selectedMode === "walking" && selectedWalkingRouteKey ? (
              <div className="mode-route-tool-route-name">
                <p className="tour-add-form-label">route name</p>
                <label className="field-label app-form-field mode-route-field">
                  <input
                    className="field-input"
                    value={activeWalkingRouteNameValue}
                    placeholder={`Write route name (${activeLanguageIso})`}
                    onChange={(event) =>
                      onChangeWalkingRouteName?.(
                        selectedWalkingRouteKey,
                        activeLanguageIso,
                        event.target.value,
                      )
                    }
                  />
                </label>
              </div>
            ) : null}
            {isWalkingRouteShapeEditEnabled ? (
              <p className="mode-route-tool-step-hint">
                Click anywhere on the map to draw the manual route. Drag orange
                points to adjust it. Use the red X to delete an adjustment
                point.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="mode-route-tool-point-list">
          {(modePoints || []).map((point, index) => {
            const namePoint =
              readLocalizedValue(point?.name?.[activeLanguageIso]) ||
              readLocalizedValue(point?.name?.en) ||
              Object.values(point?.name || {})
                .map(readLocalizedValue)
                .find(Boolean) ||
              "";
            const routeGroupDisplay =
              selectedMode === "driving" ? resolveRouteGroupDisplay(point) : "";

            return (
              <div
                key={point.id}
                className={`mode-route-tool-point-accordion ${selectedPointId === point.id ? "open" : ""}`}
              >
                {getPointValidationIssues(point, selectedMode).length ? (
                  <p className="mode-route-tool-step-hint">
                    Missing:{" "}
                    {getPointValidationIssues(point, selectedMode).join(", ")}
                  </p>
                ) : null}
                <div
                  className={`mode-route-tool-point-item ${selectedPointId === point.id ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="mode-route-tool-point-toggle"
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopWalkingRouteShapeEdit?.();
                      onSelectPoint?.(
                        selectedPointId === point.id ? "" : point.id,
                      );
                    }}
                  >
                    <small>{point.routeGroupName} - </small>
                    <span className="mode-route-tool-point-title">
                      <span className="mode-route-tool-point-name-row">
                        <span>{namePoint || `Point ${index + 1}`}</span>
                        {drivingDurationByPointId.get(String(point.id || "")) ? (
                          <small
                            className="mode-route-tool-point-duration"
                            title="Estimated driving time to the next point"
                          >
                            To next · {drivingDurationByPointId.get(String(point.id || ""))}
                          </small>
                        ) : null}
                      </span>
                      {routeGroupDisplay ? (
                        <small className="mode-route-tool-point-group">
                          detour: {routeGroupDisplay}
                        </small>
                      ) : null}
                    </span>
                  </button>
                </div>
                {selectedPointId === point.id ? (
                  <div
                    className="mode-route-tool-point-body mode-route-tool-point-body-resizable"
                    title="Drag the bottom-right corner to resize the point editor"
                  >
                    <ModeToolPointEditor
                      point={point}
                      allModePoints={modePoints}
                      selectedMode={selectedMode}
                      languages={languages}
                      activeLanguageIso={activeLanguageIso}
                      onCommitPoint={onCommitPoint}
                      onTranslatePoint={onTranslatePoint}
                      onGeneratePointImage={onGeneratePointImage}
                      onGeneratePointMusic={onGeneratePointMusic}
                      isGeneratingMusic={isGeneratingMusic && String(generatingMusicPointId) === String(point.id)}
                      onMixAudioMusic={onMixAudioMusic}
                      isMixingAudio={String(mixingPointId) === String(point.id) && String(mixingLanguageIso) === String(activeLanguageIso)}
                      isAudioMixBusy={Boolean(mixingPointId)}
                      audioMixError={audioMixErrorByPointLanguage?.[`${point.id}:${activeLanguageIso}`] || ""}
                      onUploadPointMedia={onUploadPointMedia}
                      onDeletePoint={(pointId) => {
                        onStopWalkingRouteShapeEdit?.();
                        onDeletePoint?.(pointId);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mode-route-tool-steps">
        {!canSaveModePoints && modeValidationHint ? (
          <p className="mode-route-tool-step-hint">{modeValidationHint}</p>
        ) : null}
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${isSaving ? "is-loading" : ""}`}
          aria-label="Save mode points"
          title={
            isBatchAudioBusy
              ? "Wait for the batch audio operation to finish"
              : isSaving
              ? "Saving mode points"
              : canSaveModePoints
                ? "Save mode points"
                : saveModePointsHint || "Calculate route first"
          }
          disabled={!canSaveModePoints || isSaving || isBatchAudioBusy}
          onClick={async (event) => {
            event.stopPropagation();
            if (!canSaveModePoints || isSaving || isBatchAudioBusy) return;
            if (typeof onSaveModeRedux !== "function") return;
            onStopWalkingRouteShapeEdit?.();
            setIsSaving(true);
            try {
              await onSaveModeRedux();
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {isSaving ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy />
            </>
          )}
        </button>
      </div>
    </fieldset>
  );
}

export default ModeToolPointsList;
